import { Divider } from '@dynatrace/strato-components/layouts';
import Spacings from '@dynatrace/strato-design-tokens/spacings';
import { MagnifyingGlassIcon } from '@dynatrace/strato-icons';

import { disabledActions } from './TimeseriesSelectionAreaTooltip.sty.js';
import { convertToTimeseriesArray } from './utils/convert-to-timeseries-array.js';
import { getSelectedDatapoints } from './utils/get-selected-datapoints.js';
import { isDatapointWithinSelection } from './utils/is-datapoint-within-selection.js';
import { getCustomContextMenuItems } from '../../../../core/components/interactions-overlay/utils/get-custom-context-menu-items.js';
import {
  CustomActions,
  CustomIntents,
} from '../../../../core/components/selection-area/tooltip/CustomActions.js';
import { ChartTooltip } from '../../../../core/components/tooltip/ChartTooltip/ChartTooltip.js';
import { useSelectState } from '../../../hooks/store/selectors.js';
import { useZoomToSelection } from '../../../hooks/store/state-setters.js';
import { useChartInteractions } from '../../../hooks/useChartInteractions.js';
import { useTimeseriesFilteredData } from '../../../hooks/useTimeseriesFilteredData.js';
import { useTimeseriesSelect } from '../../../hooks/useTimeseriesSelect.js';
import { useXScales } from '../../../hooks/useXScale.js';
import { getDatapointTime } from '../../../utils/timeseries.js';
import { DISPLAY_UNITS_LIMIT } from '../constants.js';

export const TimeseriesSelectionAreaTooltip = () => {
  const select = useSelectState();
  const { zoomX } = useChartInteractions();
  const timeseriesSelect = useTimeseriesSelect();
  const { currentScale: xScale } = useXScales();
  const zoomToSelection = useZoomToSelection();
  const { data, allDatapoints, datapointsMap } = useTimeseriesFilteredData();

  if (!select) {
    return null;
  }

  const [from, to] = [
    xScale.invert(select.start.relative.x),
    xScale.invert(select.end.relative.x),
  ];

  const selectedDatapoints = getSelectedDatapoints(
    allDatapoints,
    datapointsMap,
    [from, to],
  );

  const actions = timeseriesSelect?.actions;

  const customContextMenu = actions
    ? getCustomContextMenuItems(
        actions(convertToTimeseriesArray(selectedDatapoints), [from, to]),
      )
    : null;

  let totalSeries = 0;
  let totalDatapoints = 0;

  for (const series of data) {
    let isSeriesPresent = false;

    for (const datapoint of series.datapoints) {
      const datapointDate = new Date(
        getDatapointTime(datapoint.start, datapoint.end),
      );
      if (isDatapointWithinSelection(datapointDate, from, to)) {
        totalDatapoints++;

        isSeriesPresent = true;
      }
    }

    if (isSeriesPresent) {
      totalSeries++;
    }
  }

  const zoomEnabled = zoomX?.enabled;
  const hasActions = !!customContextMenu?.actions.length || zoomEnabled;

  const hasIntents = !!customContextMenu?.intents.length;

  const hasMenu = zoomEnabled || hasActions || hasIntents;

  return (
    <ChartTooltip
      position={select.end.absolute}
      style={{
        overflowY: 'auto',
        pointerEvents: 'none',
        userSelect: 'none',
      }}
    >
      <ChartTooltip.TimeFrameHeader
        from={from}
        to={to}
        limit={DISPLAY_UNITS_LIMIT}
        showDuration
      />
      <ChartTooltip.Item showDivider={hasMenu}>
        <ChartTooltip.TextHeader
          text={`${totalDatapoints} data points across ${totalSeries} series`}
        />
      </ChartTooltip.Item>
      <div className={select.finished ? undefined : disabledActions}>
        {zoomEnabled && zoomX?.enabled ? (
          <ChartTooltip.ActionItem
            text={'Zoom to selection'}
            icon={<MagnifyingGlassIcon />}
            onMouseUp={(event) => {
              event.stopPropagation();
              zoomToSelection();
            }}
            shortcut={'⏎'}
          />
        ) : null}

        <CustomActions actions={customContextMenu?.actions} />

        {hasIntents && hasActions && (
          <Divider
            orientation="horizontal"
            style={{ margin: `${Spacings.Size4} 0` }}
          />
        )}

        <CustomIntents intents={customContextMenu?.intents} />
      </div>
    </ChartTooltip>
  );
};
